//
// Copyright © 2022 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

import core, {
  type AttachedDoc,
  type Class,
  type Doc,
  type DocumentQuery,
  type Hierarchy,
  type Mixin,
  type Ref,
  type Space
} from '@hcengineering/core'
import type { Asset, IntlString, Plugin } from '@hcengineering/platform'
import { plugin } from '@hcengineering/platform'
import { AnyComponent } from '@hcengineering/ui'
import { FilterMode } from '@hcengineering/view'

export * from './analytics'

/**
 * @public
 */
export interface TagElement extends Doc {
  title: string
  targetClass: Ref<Class<Doc>>
  description: string
  color: number
  category: Ref<TagCategory>
  refCount?: number
}

/**
 * @public
 */
export type InitialKnowledge = 0 | 1 | 2

/**
 * @public
 */
export type MeaningfullKnowledge = 3 | 4 | 5

/**
 * @public
 */
export type ExpertKnowledge = 6 | 7 | 8

/**
 * @public
 */
export interface TagReference extends AttachedDoc {
  tag: Ref<TagElement>
  title: string // Copy of title for full text search, updated with trigger.
  color: number // Copy of color from tagElement. Updated with trigger.

  // If set in [8-10] it is speciality, [5-7] - meaningfull, [1-4] - initial
  weight?: InitialKnowledge | MeaningfullKnowledge | ExpertKnowledge
}

/**
 * Defined set of skills per category.
 *
 * Will be used as skill category templates or category detection
 * @public
 */
export interface TagCategory extends Doc {
  icon: Asset
  label: string
  targetClass: Ref<Class<Doc>>
  // A list of possible variants.
  tags: string[]

  // If defined, will be used for unidentified tags
  default: boolean
}

/**
 * A space (typically a project) which keeps its own set of {@link TagElement}s.
 *
 * Labels of such a space live in the space itself instead of {@link globalLabelsSpace}, so they are
 * only offered, and only visible, to the members of that space.
 *
 * @public
 */
export interface ProjectLabelsSpace extends Space {
  useProjectLabels: boolean
}

/**
 * @public
 */
export const tagsId = 'tags' as Plugin

/**
 * @public
 */
const tagsPlugin = plugin(tagsId, {
  class: {
    TagElement: '' as Ref<Class<TagElement>>,
    TagReference: '' as Ref<Class<TagReference>>,
    TagCategory: '' as Ref<Class<TagCategory>>
  },
  mixin: {
    ProjectLabelsSpace: '' as Ref<Mixin<ProjectLabelsSpace>>
  },
  icon: {
    Tags: '' as Asset,
    Level1: '' as Asset,
    Level2: '' as Asset,
    Level3: '' as Asset
  },
  component: {
    DraftTagsEditor: '' as AnyComponent,
    DocTagsEditor: '' as AnyComponent,
    TagsView: '' as AnyComponent,
    TagsEditor: '' as AnyComponent,
    TagsDropdownEditor: '' as AnyComponent,
    TagsCategoryBar: '' as AnyComponent,
    TagsAttributeEditor: '' as AnyComponent,
    TagsPresenter: '' as AnyComponent,
    LabelsPresenter: '' as AnyComponent,
    TagElementPresenter: '' as AnyComponent,
    TagsEditorPopup: '' as AnyComponent,
    ObjectsTagsEditorPopup: '' as AnyComponent
  },
  string: {
    Tags: '' as IntlString,
    AddLabel: '' as IntlString,
    TagLabel: '' as IntlString,
    UseProjectLabels: '' as IntlString,
    UseProjectLabelsDescription: '' as IntlString,
    ProjectLabels: '' as IntlString,
    GlobalLabels: '' as IntlString,
    ProjectLabel: '' as IntlString,
    GlobalLabel: '' as IntlString
  },
  category: {
    NoCategory: '' as Ref<TagCategory>
  },
  filter: {
    FilterTagsIn: '' as Ref<FilterMode>,
    FilterTagsNin: '' as Ref<FilterMode>
  }
})

/**
 * @public
 */
export default tagsPlugin

/**
 * The space holding labels shared by the whole workspace.
 *
 * Every label used to live here, and labels of spaces which did not opt into project labels
 * still do. A project which keeps its own labels leaves this space behind: labels attached to
 * its objects before it opted in stay, but nothing global is offered there any more.
 *
 * @public
 */
export const globalLabelsSpace: Ref<Space> = core.space.Workspace

/**
 * Where labels are read from and written to for a given object.
 *
 * `projectSpace` is set only when the object belongs to a space which opted into project
 * labels; otherwise the scope is the global one. Build it with {@link getLabelScope} rather
 * than by hand, so client and server agree on what a scope means.
 *
 * @public
 */
export interface LabelScope {
  projectSpace?: Ref<Space>
}

/**
 * Global scope: the only scope available before project labels were introduced.
 *
 * @public
 */
export const globalLabelScope: LabelScope = {}

/**
 * @public
 */
export function isProjectLabelsSpace (space: Space | undefined, hierarchy: Hierarchy): boolean {
  if (space === undefined) {
    return false
  }
  const mixed: boolean = hierarchy.hasMixin(space, tagsPlugin.mixin.ProjectLabelsSpace)
  if (!mixed) {
    return false
  }
  return hierarchy.as(space, tagsPlugin.mixin.ProjectLabelsSpace).useProjectLabels === true
}

/**
 * @public
 */
export function getLabelScope (space: Space | undefined, hierarchy: Hierarchy): LabelScope {
  return isProjectLabelsSpace(space, hierarchy) ? { projectSpace: (space as Space)._id } : globalLabelScope
}

/**
 * The single space labels of this scope live in: the one they are created in, and the only one
 * they may be picked from.
 *
 * A project which keeps its own labels offers nothing but its own, so that a label picked there
 * is never readable outside the project.
 *
 * @public
 */
export function getLabelSpace (scope: LabelScope): Ref<Space> {
  return scope.projectSpace ?? globalLabelsSpace
}

/**
 * Whether a label living in `elementSpace` may be attached to an object of this scope.
 *
 * @public
 */
export function canAttachLabel (elementSpace: Ref<Space>, scope: LabelScope): boolean {
  return elementSpace === getLabelSpace(scope)
}

/**
 * Query for the labels a picker of this scope may offer.
 *
 * @public
 */
export function getLabelCandidateQuery (
  scope: LabelScope,
  targetClass: Ref<Class<Doc>>,
  search: string = ''
): DocumentQuery<TagElement> {
  return { title: { $like: '%' + search + '%' }, targetClass, space: getLabelSpace(scope) }
}

/**
 * Query for the global labels a project object carries from before the project opted in.
 *
 * They are no longer on offer, but they are still attached, so the picker has to read them by id
 * to be able to show them as selected and let them be taken off. `undefined` when there is
 * nothing to read: in the global scope every selected label is a candidate already.
 *
 * The search matches the same way as {@link getLabelCandidateQuery}, which is how a selected
 * label behaves in the global scope, where it is a candidate like any other.
 *
 * @public
 */
export function getLegacyLabelQuery (
  scope: LabelScope,
  selected: Array<Ref<TagElement>>,
  search: string = ''
): DocumentQuery<TagElement> | undefined {
  if (getLabelSpace(scope) === globalLabelsSpace || selected.length === 0) {
    return undefined
  }
  return { _id: { $in: selected }, space: globalLabelsSpace, title: { $like: '%' + search + '%' } }
}

/**
 * Whether the label belongs to a project rather than to the whole workspace.
 *
 * @public
 */
export function isProjectLabel (element: Pick<Doc, 'space'>): boolean {
  return element.space !== globalLabelsSpace
}

/**
 * A group of labels as a picker shows them.
 *
 * @public
 */
export interface LabelPickerSection {
  /** Absent when the scope has a single group, which needs no caption to be told apart. */
  label?: IntlString
  elements: TagElement[]
  /** The group may only be taken from: none of its labels may be attached anew. */
  removeOnly: boolean
}

/**
 * Splits what a picker has read into the groups it shows.
 *
 * A project keeps its own labels apart from the global ones it inherited, and the caption tells
 * which is which, and hence who else can see them. The global group is made of selected labels
 * only, so that no other global label can be attached from within a project.
 *
 * @public
 */
export function buildLabelSections (
  scope: LabelScope,
  candidates: TagElement[],
  legacy: TagElement[],
  selected: Array<Ref<TagElement>>
): LabelPickerSection[] {
  const offered = candidates.filter((it) => canAttachLabel(it.space, scope))
  if (getLabelSpace(scope) === globalLabelsSpace) {
    return offered.length > 0 ? [{ elements: offered, removeOnly: false }] : []
  }
  const attached = legacy.filter((it) => !isProjectLabel(it) && selected.includes(it._id))
  return [
    { label: tagsPlugin.string.ProjectLabels, elements: offered, removeOnly: false },
    { label: tagsPlugin.string.GlobalLabels, elements: attached, removeOnly: true }
  ].filter((it) => it.elements.length > 0)
}

/**
 * @public
 */
export function findTagCategory (title: string, categories: TagCategory[]): Ref<TagCategory> {
  let defaultCategory: TagCategory | undefined
  for (const c of categories) {
    if (c.default) {
      defaultCategory = c
    }
    if (c.tags.findIndex((it) => it.toLowerCase() === title.toLowerCase()) !== -1) {
      return c._id
    }
  }
  if (defaultCategory === undefined) {
    throw new Error('Tag category not found')
  }
  return defaultCategory._id
}
